import { useState, useEffect, useRef, useCallback } from 'react';
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer.js";
import {updateSelectedGraphicsSymbols, setupMapClickHandler, zoomToPoint,  getPoints} from './utilGIS.js';
import { createPointsFromReservations } from "./utils/mapUtils.js"
import { addMapServices } from '@config/mapLayers/mapLayerUtils.js';
import { MapServices } from '@config/mapLayers/serviceMapListApp';

/**
 * Manages all ArcGIS-related logic and state.
 * @param {array} allReservations - All reservation data (static data).
 * @param {array} selectedReservations - The list of currently selected IDs (from parent hook).
 * @param {function} onMapSelect - Callback to handle a map click (calls parent's core selection logic).
 * @param {function} setMapviewCtxt - Function to update the global MapView context.
 */
const useReservationsMap = (
    allReservations,
    selectedReservations, 
    onMapSelect,          
    setMapviewCtxt
) => {
    // 1. State/Refs that manage the map/graphics
    const [mapView, setMapView] = useState(null);
    const allReservationGraphics = useRef([]);
    const graphicLayerRef = useRef(new GraphicsLayer({ id: 'reservation-points' }));
    
    // --- Lifecycle Effects ---

    // 2. EFFECT: Initialize Graphics Layer and Points (Runs once on data load)
    useEffect(() => {
        if (mapView && allReservations.length > 0) {
            // Ensure layer is on map
            if (!mapView.map.findLayerById(graphicLayerRef.current.id)) {
                mapView.map.add(graphicLayerRef.current);
            }
            // Create and add all graphics
            graphicLayerRef.current.removeAll();
            const resevPoints = createPointsFromReservations(allReservations);
            allReservationGraphics.current = resevPoints;
            graphicLayerRef.current.addMany(resevPoints);
        }
    }, [mapView, allReservations]);
    
    // 3. EFFECT: Update Symbols (Reacts to parent's state changes)
    useEffect(() => { //console.log("selectedReservations",selectedReservations);
        if (!mapView || allReservationGraphics.current.length === 0) return;
        updateSelectedGraphicsSymbols(
            allReservationGraphics.current,
            selectedReservations // <-- DEPENDENCY: Reacts to the core state
        );
    }, [mapView, selectedReservations]);

    // 4. EFFECT: Map Click Handler Setup/Cleanup
    useEffect(() => {
        if (!mapView) return;
        // Pass the selection callback to the GIS utility function
        const clickHandler = setupMapClickHandler(mapView, graphicLayerRef, onMapSelect);
        return () => {
            if (clickHandler) {
                clickHandler.remove();
            }
        };
    }, [mapView, onMapSelect]); // <-- DEPENDENCY: Depends on the selection callback

    // 5. Handler to set the map view on load
    const onarcgisViewReadyChange = useCallback((event) => {
        const { map, view } = event.target;
        setMapView(view);
        if (setMapviewCtxt) {
            setMapviewCtxt(view);
        }
        addMapServices(map, [MapServices.TRIBAL_BOUNDRY]); 
    }, [setMapviewCtxt]);

    // --- Map Actions (Exposed Public API) ---

    const zoomToSelReservations = useCallback(() => {
        if (selectedReservations.length > 0 && mapView) { 
           const pnts = getPoints(selectedReservations, allReservations);
           if(pnts) {
             zoomToPoint({mapview:mapView, loc:pnts});
           }
       }
    }, [mapView, selectedReservations, allReservations]);

    const zoomReservation = useCallback((reservation) => {
        if (reservation && reservation.location && mapView) {
            zoomToPoint({
                mapview: mapView,
                loc: [reservation.location.lng, reservation.location.lat],
                zoom: 8
            });
        }
    }, [mapView]);

    // Returns map state and map-specific actions
    return {
        mapView,
        onarcgisViewReadyChange,
        mapActions: {
            zoomToSelReservations,
            zoomReservation
        }
    };
};

export default useReservationsMap;